import React, { useEffect, useState } from 'react';
import { ExternalLink, BookOpen, HelpCircle, Activity, Package, DollarSign, Search, Calendar, ArrowLeft } from 'lucide-react';
import axios from '../api/axios';
import { formatDate } from '../utils/dateFormat';


const KnowledgeBase = () => {
  const [selectedCategory, setSelectedCategory] = useState('general');
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedArticle, setSelectedArticle] = useState(null);

  const categories = [
    {
      id: 'general',
      name: 'General Overview',
      icon: BookOpen,
      description: 'Basic system overview and getting started guides'
    },
    {
      id: 'registration',
      name: 'Users & Access',
      icon: HelpCircle,
      description: 'User management, roles, and permissions'
    },
    {
      id: 'inventory',
      name: 'Inventory',
      icon: Package,
      description: 'Parts management and stock control'
    },
    {
      id: 'operations',
      name: 'Operations',
      icon: Activity,
      description: 'Job cards, scheduling, and workflow management'
    },
    {
      id: 'accounts',
      name: 'Accounts',
      icon: DollarSign,
      description: 'Financial management and reporting'
    },
    {
      id: 'reports',
      name: 'Reports',
      icon: Activity,
      description: 'System reports and data analytics'
    }
  ];

  useEffect(() => {
    fetchArticles();
    setSelectedArticle(null); // Clear selected article when category changes
  }, [selectedCategory]);

  const fetchArticles = async () => {
    setLoading(true);
    try {
      // Use user-friendly mock data for better user experience
      setArticles(getMockArticles(selectedCategory));
    } catch (error) {
      setArticles([]);
    } finally {
      setLoading(false);
    }
  };

  const getMockArticles = (category) => {
    const mockData = {
      general: [
        {
          id: 1,
          title: 'Getting Started with AGMS',
          description: 'Learn the basics of using the Auto Garage Management System',
          lastUpdated: '2025-01-15T10:00:00Z',
          content: `# Getting Started with AGMS\n\n## System Overview\nAGMS is a comprehensive Auto Garage Management System designed to streamline your garage operations.\n\n## Main Modules:\n- **Dashboard**: View key metrics and recent activities\n- **Members**: Manage staff and user accounts\n- **Vehicles**: Track customer vehicles and service history\n- **Inventory**: Manage parts and stock levels\n- **Job Cards**: Create and track service jobs\n- **Accounts**: Handle invoicing and payments\n\n## Quick Start:\n1. Login with your credentials\n2. Set up your garage profile in Settings\n3. Add your first vehicle in the Vehicles module\n4. Create a job card for service tracking\n5. Manage inventory and generate invoices`
        },
        {
          id: 2,
          title: 'Dashboard Overview',
          description: 'Understanding your main dashboard and key metrics',
          lastUpdated: '2025-01-10T14:30:00Z',
          content: `# Dashboard Overview\n\n## Key Metrics Cards:\n- **Inquiry**: Job cards with PENDING status\n- **Pending Approval**: Jobs awaiting manager approval\n- **Ongoing**: Job cards with IN_PROGRESS status\n- **Pending Entries**: Incomplete data records\n\n## Filter Options:\n- **Date Range**: From Date and To Date selection\n- **Period**: Monthly, Weekly, or Daily view\n- **Branch**: Filter by specific branch or view all\n- **Submit Button**: Apply filters to refresh data\n\n## Charts & Analytics:\n- **Revenue Analysis**: Bar chart showing Revenue vs COGS by month\n- **Customer Analysis**: Total customers, new this month, returning\n- **Revenue Distribution**: Pie chart showing Service, Parts, Labor breakdown\n\n## Report Generation:\n- **Completed Order Report**: Export finished job cards\n- **All Orders Report**: Export all job card data\n\n## Real-time Updates:\n- Metrics refresh automatically\n- Filter-based data loading\n- Visual charts with interactive tooltips`
        },
        {
          id: 3,
          title: 'User Interface Overview',
          description: 'Navigate through the main features and interface elements',
          lastUpdated: '2025-01-08T09:15:00Z',
          content: `# User Interface Guide\n\n## Main Navigation:\n- **Sidebar Menu**: Access all system modules\n- **Top Bar**: Search, notifications, user menu\n- **Breadcrumbs**: Track your current location\n\n## Common Actions:\n- **Add Button (+)**: Create new records\n- **Edit Icon**: Modify existing data\n- **Delete Icon**: Remove records (with confirmation)\n- **Export Button**: Download data as Excel/PDF\n\n## Data Tables:\n- Click column headers to sort\n- Use filters to narrow results\n- Pagination controls at bottom\n- Bulk actions via checkboxes`
        }
      ],
      registration: [
        {
          id: 4,
          title: 'User Management',
          description: 'How to add and manage staff accounts',
          lastUpdated: '2025-05-15T11:20:00Z',
          content: `# User Management\n\n## Adding New Users:\n1. Navigate to **Members** module from the sidebar\n2. Click 'Add Member' button\n3. Fill in member details:\n   - Name, Email, Phone\n   - Role (Admin, Technician, Mechanic)\n   - Branch assignment\n   - Set password\n4. Enable 'Can Login' toggle for system access\n\n## Managing Existing Users:\n- Edit member details from the members list\n- Activate/deactivate accounts\n- Reset passwords\n- Change role assignments\n\n## Important Notes:\n- Only Admin users can create new members\n- Email must be unique across all members\n- Deactivated users cannot login but data is preserved`
        },
        {
          id: 5,
          title: 'Roles and Permissions (RBAC)',
          description: 'Understanding the role-based access control system',
          lastUpdated: '2025-05-15T16:45:00Z',
          content: `# Roles & Permissions (RBAC)\n\n## System Roles:\n\n### Admin\n- Full system access to all modules\n- Manage users, roles, and permissions\n- Access Settings, Database Management\n- View all financial data and reports\n\n### Technician / Mechanic\n- View and update assigned job cards\n- Access inventory (view parts)\n- Fill pre-service checklist\n- Upload job card photos\n- Limited to own work assignments\n\n## Permission System:\n- Permissions are module-based (e.g., jobcards.read, jobcards.create)\n- Each role has a set of assigned permissions\n- Buttons/features are hidden if user lacks permission\n- API endpoints enforce permissions server-side\n\n## Managing Permissions:\n1. Go to Settings > Roles & Permissions\n2. Select a role to edit\n3. Toggle permissions on/off\n4. Changes take effect on next login`
        }
      ],
      inventory: [
        {
          id: 6,
          title: 'Managing Parts Inventory',
          description: 'Add, update, and track automotive parts in your inventory',
          lastUpdated: '2025-01-14T13:10:00Z',
          content: `# Inventory Management System\n\n## Main Modules:\n\n### Parts Management\n- Add, edit, and delete automotive parts\n- Track stock levels and pricing\n- Category and brand organization\n- Barcode and SKU management\n\n### Services Master\n- Define service catalog\n- Set service pricing and duration\n- Category-based organization\n- Integration with job cards\n\n### Purchase Orders\n- Create orders to vendors\n- Track order status and delivery\n- Receive items and update stock\n- Vendor management integration\n\n### Purchase Requisition\n- Employee requests for parts\n- Approval workflow\n- Convert approved requests to POs\n\n### Purchase Returns\n- Return defective or excess items\n- Track return status\n- Credit note management\n\n### Stock Transfer\n- Transfer parts between branches\n- Track transfer status\n- Update stock levels automatically\n\n## Navigation:\nUse the tab system to switch between different inventory functions. Each tab provides specialized tools for that particular aspect of inventory management.`
        },
        {
          id: 7,
          title: 'Stock Alerts and Low Stock Management',
          description: 'Set up automatic alerts for low stock items',
          lastUpdated: '2025-01-11T08:30:00Z',
          content: `# Stock Alerts System\n\n## Setting Up Alerts:\n1. Go to each part's details\n2. Set 'Minimum Stock Level'\n3. Configure alert preferences in Settings\n4. Choose notification methods (email/dashboard)\n\n## Alert Types:\n- **Low Stock**: When quantity falls below minimum\n- **Out of Stock**: When quantity reaches zero\n- **Overstock**: When quantity exceeds maximum\n\n## Managing Alerts:\n- View active alerts on dashboard\n- Mark alerts as acknowledged\n- Generate reorder reports\n- Set up automatic purchase orders\n\n## Best Practices:\n- Set realistic minimum levels\n- Review and adjust thresholds regularly\n- Monitor fast-moving items closely\n- Plan for seasonal demand changes`
        },
        {
          id: 8,
          title: 'Purchase Orders and Vendor Management',
          description: 'Create purchase orders and manage supplier relationships',
          lastUpdated: '2025-01-07T15:20:00Z',
          content: `# Purchase Orders & Vendors\n\n## Vendor Management:\n1. Navigate to Vendors module\n2. Add vendor details:\n   - Company name and contact info\n   - Payment terms and conditions\n   - Preferred parts/brands\n   - Rating and notes\n\n## Creating Purchase Orders:\n1. Go to Inventory > Purchase Orders\n2. Select vendor from dropdown\n3. Add parts to order:\n   - Search and select parts\n   - Specify quantities\n   - Confirm prices\n4. Review and submit order\n\n## Order Tracking:\n- **Pending**: Order created, awaiting approval\n- **Sent**: Order sent to vendor\n- **Partial**: Some items received\n- **Complete**: All items received\n\n## Receiving Stock:\n- Update quantities received\n- Note any discrepancies\n- Generate receiving reports`
        }
      ],
      operations: [
        {
          id: 9,
          title: 'Job Card Management',
          description: 'Complete guide to job card creation and workflow',
          lastUpdated: '2025-01-13T12:00:00Z',
          content: `# Job Card Management\n\n## Creating Job Cards:\n1. Navigate to Job Cards module\n2. Click 'Create Job Card' button\n3. Fill in the form across tabs:\n   - **Job Details**: Customer, vehicle, branch, technician, services, parts, appointment\n   - **Sketch**: Mark vehicle damage points on the diagram\n   - **Photos**: Upload before/after photos (max 10)\n   - **Checklist**: Fill pre-service checklist (fluids, condition, functional)\n\n## Job Card Status Flow:\n- **PENDING**: Initial creation, awaiting start\n- **WAITING_FOR_PARTS**: Out-of-stock parts needed (auto-creates Purchase Requisition)\n- **IN_PROGRESS**: Work is actively being performed\n- **COMPLETED**: All work finished, ready for delivery\n- **CANCELLED**: Job cancelled\n\n## Pre-Service Checklist:\n- **Fluid Levels**: Fuel, Engine Oil, Coolant, Brake Fluid, etc. (E to F gauge)\n- **Condition Checks**: Battery, Tires, Brakes, Wipers, etc. (OK/Issue toggle)\n- **Functional & Handover**: Headlights, AC, Windows, Keys Received, etc.\n\n## Out-of-Stock Parts:\n- If a part has zero stock, job card is set to WAITING_FOR_PARTS\n- A Purchase Requisition is automatically created for the missing parts\n- Parts with sufficient stock are deducted normally\n\n## Key Features:\n- Real-time status updates\n- Vehicle damage sketch with touch support\n- Photo upload (before/after)\n- Pre-service checklist (saved per job card)\n- Print layout fits one A4 page dynamically\n- Customer and vehicle linking\n- Technician assignment\n\n## Filtering & Search:\n- Search by order number, customer name, or plate number\n- Filter by status\n- Filter by technician\n- Date range filtering`
        },
        {
          id: 10,
          title: 'Vehicle Management',
          description: 'Managing customer vehicles and service history',
          lastUpdated: '2025-01-06T10:45:00Z',
          content: `# Vehicle Management (Vehicle Master)\n\n## Adding Vehicles:\n1. Navigate to Vehicle Master module\n2. Click 'Add Vehicle' button\n3. Enter vehicle details:\n   - Make and Model (from brand database)\n   - Year, Color, Plate Number\n   - VIN Number, Mileage\n   - Customer assignment\n   - Status (Active, Inactive, Maintenance, Sold)\n\n## Vehicle Information Display:\n- **Brand Logos**: Visual brand identification\n- **Customer Details**: Owner name and contact\n- **Status Indicators**: Color-coded status badges\n- **Registration Info**: Plate number, VIN\n- **Service History**: Linked job cards\n\n## Search & Filtering:\n- Search by make, model, or plate number\n- Filter by make/brand\n- Filter by customer\n- Filter by status\n- Pagination for large datasets\n\n## Vehicle Actions:\n- **View Details**: Complete vehicle information\n- **Edit**: Modify vehicle details\n- **Delete**: Remove vehicle (with confirmation)\n- **Service History**: View all related job cards\n\n## Integration Features:\n- Links to customer records\n- Integration with job card system\n- Brand logo management\n- Status tracking for maintenance scheduling`
        }
      ],
      accounts: [
        {
          id: 11,
          title: 'Financial Reporting and Analytics',
          description: 'Generate reports and analyze your business performance',
          lastUpdated: '2025-01-05T14:15:00Z',
          content: `# Financial Reports & Analytics\n\n## Available Reports:\n\n### Revenue Reports\n- Daily/Monthly/Yearly sales\n- Service vs Parts revenue\n- Payment method breakdown\n- Customer payment history\n\n### Expense Reports\n- Operating expenses tracking\n- Parts purchase costs\n- Staff payroll expenses\n- Overhead cost analysis\n\n### Profit & Loss\n- Gross profit margins\n- Net profit calculations\n- Cost center analysis\n- Trend comparisons\n\n## Generating Reports:\n1. Navigate to Reports module\n2. Select report type\n3. Choose date range\n4. Apply filters (customer, service type, etc.)\n5. Generate and export (PDF/Excel)\n\n## Key Metrics Dashboard:\n- Revenue trends\n- Top customers\n- Best-selling services\n- Technician performance\n- Inventory turnover rates`
        },
        {
          id: 12,
          title: 'Invoice Management',
          description: 'Create, send, and track customer invoices',
          lastUpdated: '2025-01-04T09:30:00Z',
          content: `# Invoice Management\n\n## Creating Invoices:\n1. Navigate to Accounts > Invoices\n2. Click 'Create Invoice'\n3. Select customer and link to job card/quotation\n4. Review services and parts (auto-populated from job card)\n5. Apply discounts if needed\n6. Set due date and payment terms\n\n## Invoice Details:\n- **Header**: Company logo, contact info, invoice number\n- **Customer Info**: Name, contact, address\n- **Vehicle Info**: Make, model, plate number\n- **Line Items**: Services, parts with quantities and prices\n- **Calculations**: Subtotal, discount, VAT, total\n- **Terms & Conditions**: Configurable in Settings\n\n## Invoice Payment Status:\n- **Unpaid**: No payment received yet\n- **Partial**: Some payment received, balance remaining\n- **Paid**: Fully paid\n- **Overdue**: Past due date and still unpaid\n\n## Payment Processing:\n- Record cash/card/bank transfer payments\n- Track partial payments with running balance\n- Generate payment receipts\n- Automatic status update on payment\n\n## Batch Print:\n- Select multiple invoices using checkboxes\n- Click 'Print with Summary' for statement of account\n- System groups by customer automatically\n- Each customer gets their own summary page + individual invoices\n\n## Print Features:\n- Professional layout with company branding\n- Dynamic sizing to fit one page\n- Dual signature blocks\n- Terms & conditions included`
        }
      ],
      reports: [
        {
          id: 13,
          title: 'Reports Overview',
          description: 'Complete guide to all system reports and their data sources',
          lastUpdated: '2025-01-30T12:00:00Z',
          content: `# Reports Overview\n\n## Financial Reports:\n\n### ✅ Trial Balance\n- Uses actual accounts and transactions with aggregation\n- Shows opening balances, period movements, and closing balances\n- Real-time calculation from accounts and transactions tables\n\n### ✅ Balance Sheet\n- Real receivables, payables, inventory, and cash from database\n- Accounts receivable from unpaid invoices\n- Accounts payable from outstanding purchase orders\n- Inventory value calculated from parts table\n- Cash balances from accounts table\n\n### ✅ P&L Report\n- Actual revenue from invoices and expenses by category\n- Service revenue calculated from invoice totals\n- Expenses grouped by categories from expense_categories table\n- Net profit calculated as revenue minus expenses\n\n### ✅ Cash Accounts\n- Real account balances and transaction counts\n- Shows all ASSET type accounts with cash-related names\n- Transaction counts from actual database records\n\n### ✅ Journal\n- Actual transactions with account names\n- Complete transaction history with debit/credit entries\n- Account names joined from accounts table\n\n### ✅ VAT Report\n- Real VAT data from invoices with organization settings\n- VAT percentage from organization configuration\n- Running balance calculations from actual invoice tax amounts\n\n## Customer & Sales Reports:\n\n### ✅ Customer Receivable\n- Unpaid invoices with customer data\n- Shows invoices where payment status is not 'Paid'\n- Outstanding balances calculated from total minus paid amounts\n\n### ✅ Sales Order\n- Actual invoices with payment status\n- Complete invoice data with customer and branch information\n- Payment tracking and balance calculations\n\n### ✅ Sales Return\n- Real return data with customer info\n- Links to job cards and customer records\n- Return status and refund amount tracking\n\n### ✅ Job Margin\n- Calculated margins from actual invoice data\n- Revenue from invoice totals\n- Estimated costs and profit margin calculations\n\n## Vendor & Purchase Reports:\n\n### ✅ Vendor Payable\n- Outstanding purchase orders\n- Shows unpaid purchase orders with vendor information\n- Amount owed calculations from purchase order totals\n\n### ✅ Purchase Report\n- Real purchase orders with vendor data\n- Complete purchase history with vendor names\n- Purchase amounts and order details\n\n## Inventory & Operations Reports:\n\n### ✅ Inventory Report\n- Actual parts with current stock levels\n- Real quantity data from parts table\n- Vendor information and part categories\n\n### ✅ Order Report\n- Real job cards with status breakdown\n- Actual job card data with customer and vehicle information\n- Status counts and total value calculations\n\n### ✅ All Orders Report\n- Comprehensive job card analytics\n- Complete job card statistics with customer data\n- Status breakdowns and top customer analysis\n\n## Important Notes:\n\n- **After System Reset**: All reports will show zero/empty data until new transactions are created\n- **Real-Time Data**: Reports reflect current database state\n- **No Mock Data**: All hardcoded/sample data has been removed\n- **Database Queries**: All reports use raw SQL queries for accuracy\n- **Performance**: Reports are optimized for large datasets\n\n## Troubleshooting:\n\n- If reports show no data after reset, this is expected behavior\n- Create sample transactions to test report functionality\n- Check database connections if reports fail to load\n- Verify user permissions for report access`
        }
      ]
    };
    
    return mockData[category] || [];
  };

  const filteredArticles = (articles || []).filter(article =>
    article.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    article.description.toLowerCase().includes(searchTerm.toLowerCase())
  );



  const handleArticleClick = (article) => {
    setSelectedArticle(article);
  };

  const handleBackToList = () => {
    setSelectedArticle(null);
  };

  return (
    <div className="h-screen flex flex-col md:flex-row bg-gray-50">
      {/* Left Sidebar - Categories */}
      <div className="md:w-80 bg-white border-b md:border-b-0 md:border-r border-gray-200 flex-shrink-0 flex flex-col">
        <div className="p-4 md:p-6 border-b border-gray-200">
          <h1 className="text-lg md:text-xl font-semibold text-gray-900 mb-3 md:mb-4">Knowledge Base</h1>
          
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              type="text"
              placeholder="Search articles..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>

        {/* Categories - horizontal scroll on mobile */}
        <div className="flex md:block overflow-x-auto md:overflow-x-visible md:flex-1 md:overflow-y-auto">
          <div className="flex md:block p-3 md:p-4 gap-2 md:gap-0">
            {categories.map(category => {
              const Icon = category.icon;
              return (
                <button
                  key={category.id}
                  onClick={() => setSelectedCategory(category.id)}
                  className={`flex-shrink-0 md:w-full text-left p-2 md:p-3 rounded-lg transition-colors mb-0 md:mb-1 whitespace-nowrap md:whitespace-normal ${
                    selectedCategory === category.id
                      ? 'bg-blue-50 text-blue-700 border border-blue-200'
                      : 'text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  <div className="flex items-center md:items-start space-x-2 md:space-x-3">
                    <Icon className={`w-4 h-4 md:w-5 md:h-5 md:mt-0.5 flex-shrink-0 ${
                      selectedCategory === category.id ? 'text-blue-600' : 'text-gray-400'
                    }`} />
                    <div className="min-w-0">
                      <div className="font-medium text-sm md:text-base">{category.name}</div>
                      <div className="text-xs text-gray-500 mt-1 hidden md:block">
                        {category.description}
                      </div>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Right Panel - Articles */}
      <div className="flex-1 flex flex-col">
        {/* Header */}
        <div className="bg-white border-b border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold text-gray-900">
                {categories.find(cat => cat.id === selectedCategory)?.name}
              </h2>
              <p className="text-gray-600 mt-1">
                {categories.find(cat => cat.id === selectedCategory)?.description}
              </p>
            </div>
            <div className="text-sm text-gray-500">
              {filteredArticles.length} article{filteredArticles.length !== 1 ? 's' : ''}
            </div>
          </div>
        </div>

        {/* Articles List or Article Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {selectedArticle ? (
            // Article Content View
            <div>
              <button
                onClick={handleBackToList}
                className="mb-4 flex items-center text-blue-600 hover:text-blue-800 transition-colors"
              >
                <ExternalLink className="w-4 h-4 mr-2 transform rotate-180" />
                Back to articles
              </button>
              <div className="bg-white border border-gray-200 rounded-lg p-8">
                <h1 className="text-3xl font-bold text-gray-900 mb-4">{selectedArticle.title}</h1>
                <div className="flex items-center text-sm text-gray-500 mb-6">
                  <Calendar className="w-4 h-4 mr-1" />
                  <span>Last updated: {formatDate(selectedArticle.lastUpdated)}</span>
                </div>
                <div className="prose max-w-none">
                  {selectedArticle.content.split('\n').map((line, index) => {
                    if (line.startsWith('# ')) {
                      return <h1 key={index} className="text-2xl font-bold mt-8 mb-4 text-gray-900">{line.substring(2)}</h1>;
                    } else if (line.startsWith('## ')) {
                      return <h2 key={index} className="text-xl font-semibold mt-6 mb-3 text-gray-800">{line.substring(3)}</h2>;
                    } else if (line.startsWith('### ')) {
                      return <h3 key={index} className="text-lg font-medium mt-4 mb-2 text-gray-700">{line.substring(4)}</h3>;
                    } else if (line.startsWith('- ')) {
                      return <li key={index} className="ml-4 mb-1 text-gray-600">{line.substring(2)}</li>;
                    } else if (line.startsWith('   - ')) {
                      return <li key={index} className="ml-8 mb-1 text-gray-600">{line.substring(5)}</li>;
                    } else if (line.trim() === '') {
                      return <br key={index} />;
                    } else {
                      return <p key={index} className="mb-3 text-gray-600 leading-relaxed">{line}</p>;
                    }
                  })}
                </div>
              </div>
            </div>
          ) : loading ? (
            <div className="space-y-4">
              {Array.from({ length: 5 }).map((_, index) => (
                <div key={index} className="animate-pulse">
                  <div className="bg-gray-200 h-6 rounded mb-2"></div>
                  <div className="bg-gray-200 h-4 rounded mb-2"></div>
                  <div className="bg-gray-200 h-3 rounded w-1/3"></div>
                </div>
              ))}
            </div>
          ) : filteredArticles.length === 0 ? (
            <div className="text-center py-12">
              <BookOpen className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                {searchTerm ? 'No articles found' : 'No articles available'}
              </h3>
              <p className="text-gray-600">
                {searchTerm 
                  ? 'Try adjusting your search terms or browse other categories.'
                  : 'Articles for this category will be added soon.'
                }
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {filteredArticles.map(article => (
                <div
                  key={article.id}
                  className="bg-white border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow cursor-pointer"
                  onClick={() => handleArticleClick(article)}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-gray-900 mb-2 hover:text-blue-600 transition-colors">
                        {article.title}
                      </h3>
                      <p className="text-gray-600 mb-3 line-clamp-2">
                        {article.description}
                      </p>
                      <div className="flex items-center text-sm text-gray-500">
                        <Calendar className="w-4 h-4 mr-1" />
                        <span>Last updated: {formatDate(article.lastUpdated)}</span>
                      </div>
                    </div>
                    <div className="ml-4 flex-shrink-0">
                      <ExternalLink className="w-5 h-5 text-gray-400 hover:text-blue-600 transition-colors" />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default KnowledgeBase;
